import React, {useEffect, useState} from "react";
import {calculatePaastPenatly, deleteInventoryForWorkshop, deleteLoan, getLoansByWorkshop} from "../../Api/Api";
import {useWorkshop} from "../Common/WorkshopContext";
import {Link, useNavigate} from "react-router-dom";
import Pagination from "../Pagination/Pagination";
import {FaExclamationTriangle, FaEye, FaSearch} from "react-icons/fa";
import {useNotification} from "../Notification/NotificationContext";
import ConfirmationModal from "../Inventory/DeleteInventoryModal";

const LoansList = () => {
    const { workshopId } = useWorkshop();
    const navigate = useNavigate();
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [showConfirmDelete, setShowConfirmDDelete] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const pageSize = 10;
    const { showNotification } = useNotification();

    useEffect(() => {
        fetchLoans(currentPage);
    }, [currentPage,searchTerm]);

    const fetchLoans = (page) => {
        setLoading(true);
        getLoansByWorkshop(workshopId, pageSize, page,searchTerm)
            .then((response) => {
                setLoans(response.data.content);
                setTotalPages(response.data.totalPages);
                setTotalCount(response.data.totalElements)
                setLoading(false);
            })
            .catch((error) =>  {
                setLoading(false)
                showNotification(error.message, "error")
            });
    };


    const calculatePenalty = async (loanId) => {
        try {

            calculatePaastPenatly(loanId)
                .then((response) => {
                    fetchLoans(); // Refresh data
                    showNotification("Penalty Applied!", "success")
                })
                .catch((error) => {
                    setLoading(false)
                    showNotification(error.message, "error")
                });

        } catch (error) {
            // console.error("Error calculating penalty", error);
            // alert("Failed to calculate penalty");
            showNotification(error.message, "error")
        }
    };


    const handleDeleteClick = (id) => {
        setSelectedItemId(id);
        setShowConfirmDDelete(true); // Show the confirmation modal
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteLoan(selectedItemId); // Call the API
            setShowConfirmDDelete(false); // Close the modal
            setSelectedItemId(null);
            fetchLoans(currentPage); // Refresh loan list
            showNotification("Loan deleted succeessfully!", "success")
        } catch (error) {
            showNotification(error.message, "error")
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDDelete(false); // Close the modal
        setSelectedItemId(null);
    };


    return (
        <div className="mt-6">
            <div className="max-w-screen-xl mx-auto">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    {/* Header Section */}
                    <div className="bg-teal-400 text-white p-3 rounded-t-lg flex justify-between items-center">
                        <h2 className="text-xl font-semibold">Loan List</h2>
                        <div className="flex gap-2 items-center w-full sm:w-auto">
                            <div className="relative w-full sm:w-64">
                                <input
                                    type="text"
                                    placeholder="Search by name"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 rounded-md focus:outline-none text-gray-700"
                                />
                                <FaSearch className="absolute left-3 top-2.5 text-gray-400" />
                            </div>
                        </div>

                    </div>

                    {/* Main Content */}
                    <div className="p-6">
                        {loading ? (
                            <div className="flex justify-center items-center space-x-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto shadow-md rounded-lg">
                                    <table className="min-w-full bg-white">
                                        <thead className="bg-gray-50 text-gray-800">
                                        <tr>
                                            <th className="p-2 text-center">Loan ID</th>
                                            <th className="p-2 text-left">Name</th>
                                            <th className="p-2 text-left">Amount</th>
                                            <th className="p-2 text-left">Type</th>
                                            <th className="p-2 text-left">Down Payment</th>
                                            <th className="p-2 text-left">Tenure</th>
                                            <th className="p-2 text-left">ROI</th>
                                            <th className="p-2 text-left">Start Date</th>
                                            <th className="p-2 text-left">Status</th>
                                            <th className="p-2 text-left">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {loans.length > 0 ? (
                                            loans.map((loan) => (
                                                <tr key={loan.id} className="border-b hover:bg-gray-100">
                                                    <td className="p-1 text-center">{loan.id}</td>
                                                    <td className="p-1 hover:underline">
                                                        <Link to={`/customers/${loan.customer.id}/workshops/${workshopId}/profile`}>{loan.customer.name}</Link>
                                                    </td>
                                                    <td className="p-1">&#x20B9; {loan.loanAmount.toFixed(2)}</td>
                                                    <td className="p-1 text-center">{loan.loanType}</td>
                                                    <td className="p-1">&#x20B9; {loan.downPayment.toFixed(2)}</td>
                                                    <td className="p-1">{loan.tenureInMonths} Months</td>
                                                    <td className="p-1">{loan.interestRate}%</td>
                                                    <td className="p-1">{new Date(loan.startDate).toLocaleDateString()}</td>
                                                    <td className="p-1">
                                                            <span className={`px-3 py-1 rounded-full text-sm ${loan.status === "ACTIVE" ? "bg-green-200 text-green-800" : "bg-yellow-200 text-yellow-800"}`}>
                                                                {loan.status}
                                                            </span>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                className="bg-teal-500 text-white px-3 py-1 rounded-md text-sm hover:bg-teal-600 flex items-center"
                                                                onClick={() => navigate(`/loans/${loan.id}`)}
                                                            >
                                                                <FaEye className="mr-1" />
                                                                View
                                                            </button>

                                                            {loan.status != 'CLOSED' && (
                                                                <button
                                                                    className="bg-red-500 text-white px-3 py-1 rounded-md text-sm hover:bg-red-600 flex items-center"
                                                                    onClick={() => handleDeleteClick(loan.id)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            )}

                                                        </div>
                                                        {/*<button*/}
                                                        {/*    className="bg-yellow-500 text-white px-4 py-2 rounded-md flex items-center"*/}
                                                        {/*    onClick={async () => await calculatePenalty(loan.id)}*/}
                                                        {/*    hidden={loan.pastPenaltyApplied} // Hide if condition not met*/}
                                                        {/*>*/}
                                                        {/*    <FaExclamationTriangle className="text-lg" />*/}
                                                        {/*    <span className="ml-2">Penalty</span>*/}
                                                        {/*</button>*/}

                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-4 text-gray-500">
                                                    No loans found for this workshop.
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination UI */}
                                <div className="flex justify-start mt-2">
                                    <Pagination
                                        totalPages={totalPages}
                                        onPageChange={setCurrentPage}
                                        currentPage={currentPage}
                                        totalCount={totalCount}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <ConfirmationModal
                show={showConfirmDelete}
                message="Are you sure you want to delete this loan ? This action cannot be undone."
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default LoansList;
